/* =========================================================================
   Charm Bar — Foundation Script
   =========================================================================
   File: assets/charm-bar.js (paste into theme assets folder via Shopify
   admin: Online Store > Themes > [your duplicated theme] > ... > Edit code >
   Assets > Add a new asset > "charm-bar.js")

   Loaded by: the Charm Bar templates (page.charm-bar.json,
   collection.charm-bar-step-1/2/3.json) via:
     <script src="{{ 'charm-bar.js' | asset_url }}" defer></script>

   What this script does:
     1. Reads window.__charmBarStepMap (handle -> step number) emitted by
        the progress bar Liquid section, so it can identify which Charm Bar
        step each cart item belongs to.
     2. On page load AND on cart change, fetches /cart.js and recomputes:
          - which progress chips should be in the "filled" state
          - which step's Next button should be in green/grey state
          - whether the Skip text link should be visible or hidden
          - the active-filter count badge on the mobile filter pill
     3. Wires the View Cart button to open the theme's native cart drawer.
     4. Wires the mobile filter pill to dispatch a click on the existing
        theme facets-drawer trigger.
     5. Injects the dual-button card actions block under every product-card
        on Charm Bar collection pages.

   Defensive design: every DOM query is null-checked. Every tag/handle
   lookup is normalized. The script is wrapped in an IIFE and idempotent
   (safe to re-run if the theme re-injects it).
   ========================================================================= */

(function () {
  'use strict';

  // --- Idempotency guard ----------------------------------------------------
  if (window.__charmBarBound) return;
  window.__charmBarBound = true;

  // --- Config ---------------------------------------------------------------
  // window.__charmBarStepMap is emitted by sections/charm-bar-progress.liquid
  // Shape: { "product-handle-1": 1, "product-handle-2": 2, ... }
  // Step numbers: 1 = chain, 2 = attachment, 3 = charm
  // If the map isn't present (e.g., on a non-flow page), bail gracefully.
  var stepMap = window.__charmBarStepMap || {};

  // --- Helpers --------------------------------------------------------------

  /** Normalize a Shopify product handle (lowercase, trim) */
  function normalizeHandle(h) {
    return (h || '').toString().trim().toLowerCase();
  }

  /** Fetch the live cart from /cart.js and parse JSON */
  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js fetch failed: ' + res.status);
      return res.json();
    });
  }

  /** Given a cart, return { 1: itemOrNull, 2: itemOrNull, 3: itemOrNull } */
  function bucketCartByStep(cart) {
    var buckets = { 1: null, 2: null, 3: null };
    if (!cart || !Array.isArray(cart.items)) return buckets;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var handle = normalizeHandle(item.handle || (item.product && item.product.handle));
      var step = stepMap[handle];
      if (step && !buckets[step]) {
        buckets[step] = item;
      }
    }
    return buckets;
  }

  // --- Progress bar state updater -----------------------------------------

  function updateProgressChips(buckets) {
    var bar = document.querySelector('[data-charm-bar-progress]');
    if (!bar) return;

    [1, 2, 3].forEach(function (step) {
      var chip = bar.querySelector('[data-step="' + step + '"]');
      if (!chip) return;

      var item = buckets[step];
      var nameEl = chip.querySelector('.cb-progress__product');
      var numEl = chip.querySelector('.cb-progress__num');

      if (item) {
        chip.classList.add('is-filled');
        // Swap the step number for a checkmark when filled
        if (numEl) numEl.textContent = '✓';  // ✓
        var title = (item.product_title || item.title || '').replace(/\s+\-\s+.*$/, '');
        if (nameEl) {
          nameEl.textContent = title;
        } else {
          var span = document.createElement('span');
          span.className = 'cb-progress__product';
          span.textContent = title;
          chip.appendChild(span);
        }
      } else {
        chip.classList.remove('is-filled');
        // Restore the step number when no longer filled
        if (numEl) numEl.textContent = String(step);
        if (nameEl) nameEl.remove();
      }
    });
  }

  // --- Action bar (Next button + Skip link) state updater -----------------

  function updateActionBar(buckets) {
    var actionBar = document.querySelector('[data-charm-bar-action-bar]');
    if (!actionBar) return;

    var stepAttr = actionBar.getAttribute('data-step') || '';
    var stepNum = parseInt(stepAttr.replace('step_', ''), 10);
    if (!stepNum || stepNum === 3) return;  // step 3 has only View Cart

    var filled = !!buckets[stepNum];

    var nextBtn = actionBar.querySelector('[data-charm-bar-next]');
    if (nextBtn) {
      if (filled) {
        nextBtn.classList.remove('cb-button--grey');
        nextBtn.classList.add('cb-button--primary');
      } else {
        nextBtn.classList.remove('cb-button--primary');
        nextBtn.classList.add('cb-button--grey');
      }
    }

    var skipLink = actionBar.querySelector('[data-charm-bar-skip]');
    if (skipLink) {
      skipLink.style.display = filled ? 'none' : '';
    }
  }

  // --- Mobile filter trigger — count badge --------------------------------

  function updateFilterCountBadge() {
    var trigger = document.querySelector('.cb-filter-trigger');
    if (!trigger) return;
    var countEl = trigger.querySelector('.cb-filter-trigger__count');
    if (!countEl) return;

    // Count how many filter URL params are currently applied (excluding price)
    var search = window.location.search;
    if (!search) { countEl.setAttribute('data-count', '0'); countEl.textContent = '0'; return; }
    var matches = search.match(/[?&]filter\.(?!v\.price\.)[^=]+=[^&]+/g);
    var n = matches ? matches.length : 0;
    countEl.setAttribute('data-count', String(n));
    countEl.textContent = String(n);
  }

  // --- View Cart button — open theme cart drawer --------------------------

  function openCartDrawer() {
    // The Prestige theme's <cart-drawer> custom element exposes .show()
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      try { drawer.show(); return; } catch (e) { /* fall through */ }
    }
    // Try to dispatch the theme's native open event as a fallback
    var fallbackTrigger = document.querySelector('[data-cart-drawer-trigger-native]');
    if (fallbackTrigger) {
      try { fallbackTrigger.click(); return; } catch (e) { /* fall through */ }
    }
    // Last resort: navigate to /cart
    window.location.href = '/cart';
  }

  // --- Mobile filter trigger pill — open theme facets-drawer --------------

  function openFiltersDrawer() {
    // The theme's facets-drawer is opened via a button with
    // aria-controls="facets-drawer" — find one and dispatch a click
    var nativeTrigger = document.querySelector(
      'button[aria-controls="facets-drawer"], .collection-toolbar__button[aria-controls*="facet"]'
    );
    if (nativeTrigger) {
      nativeTrigger.click();
      return;
    }
    // Fallback: try to find facets-drawer itself and call .show()
    var drawer = document.querySelector('facets-drawer, [is="facets-drawer"]');
    if (drawer && typeof drawer.show === 'function') {
      try { drawer.show(); return; } catch (e) { /* nothing more to do */ }
    }
  }

  // --- Inject dual-button card actions on collection pages ----------------

  function injectCardActions() {
    // Per-step card action variations:
    //   Step 1 (Chains): NO buttons on cards. Buyers tap image/title to
    //     open the modal, pick their size, and tap Select inside the
    //     modal to add. Chain cards are visually clean — no card-level
    //     CTA — because picking a size is required before any add can
    //     happen anyway.
    //   Step 2 (Attachment): single "Select" button → DIRECT ADD bypassing
    //     modal. Attachment products are single-variant; one tap adds.
    //     Image/title clicks still open the modal for buyers who want
    //     details before deciding.
    //   Step 3 (Charms): single "Select" button → SMART ADD. If the
    //     charm has multiple variants (Letter charm, Birthstone charm),
    //     opens the modal so the buyer can pick. Otherwise direct-adds.
    //     Image/title clicks open the modal for details.
    var isStep1 = /\/collections\/charm-bar-select-chain/.test(location.pathname);
    var isStep2 = /\/collections\/attachment-style/.test(location.pathname);

    // Step 1: skip injection entirely. No buttons anywhere on chain cards.
    if (isStep1) return;

    document.querySelectorAll('product-card').forEach(function (card) {
      // Already injected? skip
      if (card.querySelector('[data-charm-bar-card-actions]')) return;

      var info = card.querySelector('.product-card__info');
      if (!info) return;

      var actions = document.createElement('div');
      actions.className = 'cb-card-actions cb-card-actions--single-button';
      actions.setAttribute('data-charm-bar-card-actions', '');

      if (isStep2) {
        actions.innerHTML =
          '<button type="button" class="cb-button cb-button--primary cb-card-actions__direct-add">Select</button>';
      } else {
        // Step 3 (Charms)
        actions.innerHTML =
          '<button type="button" class="cb-button cb-button--primary cb-card-actions__add">Add</button>';
      }

      // Append after the info block, inside the card
      info.parentNode.insertBefore(actions, info.nextSibling);
    });
  }

  // --- Custom Quick-Buy Modal ---------------------------------------------
  // Opens for ALL products on Charm Bar pages (single- and multi-variant)
  // so the modal experience is consistent. Fetches /products/{handle}.js
  // for product data, populates the shell rendered by
  // sections/charm-bar-progress.liquid, then shows it.

  var modalState = {
    product: null,
    selectedOptions: {},
    currentVariant: null,
    currentMediaIndex: 0
  };

  function getModal() {
    return document.querySelector('[data-cb-modal]');
  }

  function formatPrice(cents) {
    if (typeof cents !== 'number') return '';
    var dollars = cents / 100;
    return '$' + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
  }

  // Shopify's /products/{handle}.js returns `options` as either an array
  // of strings (older API) or an array of objects with .name (newer API).
  // Normalize to a string for both cases so we don't render "[object Object]".
  function getOptionName(opt) {
    if (typeof opt === 'string') return opt;
    if (opt && typeof opt === 'object' && opt.name) return opt.name;
    return '';
  }

  function findVariantForOptions() {
    if (!modalState.product) return null;
    var optionNames = modalState.product.options.map(getOptionName);
    return modalState.product.variants.find(function (v) {
      return optionNames.every(function (name, i) {
        return v.options[i] === modalState.selectedOptions[name];
      });
    }) || null;
  }

  function updateMediaTrack() {
    var modal = getModal();
    if (!modal) return;
    var track = modal.querySelector('[data-cb-modal-track]');
    if (!track) return;
    track.style.transform = 'translateX(-' + (modalState.currentMediaIndex * 100) + '%)';
    var total = track.children.length;
    var counter = modal.querySelector('[data-cb-modal-counter]');
    if (counter) {
      counter.textContent = (modalState.currentMediaIndex + 1) + ' / ' + total;
      counter.style.display = total > 1 ? '' : 'none';
    }
    var prev = modal.querySelector('[data-cb-modal-prev]');
    var next = modal.querySelector('[data-cb-modal-next]');
    if (prev) {
      prev.disabled = modalState.currentMediaIndex === 0;
      prev.style.display = total > 1 ? '' : 'none';
    }
    if (next) {
      next.disabled = modalState.currentMediaIndex >= total - 1;
      next.style.display = total > 1 ? '' : 'none';
    }
  }

  function updatePrice() {
    var modal = getModal();
    if (!modal || !modalState.currentVariant) return;
    modal.querySelector('[data-cb-modal-price]').textContent = formatPrice(modalState.currentVariant.price);
  }

  // --- Reviews (Judge.me) -------------------------------------------------
  // Cache product handle -> rating object so repeat opens of the same
  // product don't re-fetch the PDP HTML.
  var ratingCache = {};

  function fetchAndRenderRating(handle, ratingEl) {
    if (!handle || !ratingEl) return;

    function render(rating) {
      if (!rating || !rating.count) {
        ratingEl.innerHTML = '';
        return;
      }
      var avg = Math.round(rating.avg * 10) / 10;
      var fullStars = Math.floor(rating.avg);
      var hasHalf = (rating.avg - fullStars) >= 0.25 && (rating.avg - fullStars) < 0.75;
      // Build star SVGs that look clean (vs Unicode which renders inconsistently)
      var starsHtml = '';
      for (var i = 0; i < 5; i++) {
        var filled = i < fullStars;
        var half = i === fullStars && hasHalf;
        starsHtml += '<svg class="cb-modal__star' + (filled ? ' is-full' : (half ? ' is-half' : '')) + '" '
          + 'viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">'
          + '<path d="M12 2 L14.9 8.6 L22 9.3 L16.5 14 L18.2 21 L12 17.3 L5.8 21 L7.5 14 L2 9.3 L9.1 8.6 Z" '
          + 'fill="currentColor" stroke="currentColor" stroke-width="0.5" stroke-linejoin="round"/>'
          + '</svg>';
      }
      // Match Judge.me's exact format on the PDP: "267 REVIEWS" (uppercase
      // is applied via CSS text-transform).
      var countText = rating.count + ' review' + (rating.count === 1 ? '' : 's');
      ratingEl.innerHTML =
        '<span class="cb-modal__rating-stars" aria-label="' + avg + ' out of 5 stars">' + starsHtml + '</span>'
        + '<span class="cb-modal__rating-count">' + countText + '</span>';
    }

    if (ratingCache[handle]) {
      render(ratingCache[handle]);
      return;
    }

    fetch('/products/' + handle, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('PDP fetch failed');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // Look for any Judge.me badge with the rating data attributes
        var badge = doc.querySelector('[data-average-rating][data-number-of-reviews]');
        if (!badge) {
          ratingCache[handle] = { count: 0, avg: 0 };
          render(ratingCache[handle]);
          return;
        }
        var avg = parseFloat(badge.getAttribute('data-average-rating')) || 0;
        var count = parseInt(badge.getAttribute('data-number-of-reviews'), 10) || 0;
        ratingCache[handle] = { count: count, avg: avg };
        render(ratingCache[handle]);
      })
      .catch(function (err) {
        if (window.__charmBarDebug) console.warn('[charm-bar] rating fetch failed:', err);
      });
  }

  function buildVariantOptionEl(optionName, optionValue, isSelected, isUnavailable) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cb-modal__variant-option';
    btn.dataset.optionName = optionName;
    btn.dataset.optionValue = optionValue;
    btn.textContent = optionValue;
    if (isSelected) btn.dataset.selected = 'true';
    if (isUnavailable) btn.dataset.unavailable = 'true';
    return btn;
  }

  function populateModal() {
    var modal = getModal();
    var product = modalState.product;
    if (!modal || !product) return;

    // Title
    var titleEl = modal.querySelector('[data-cb-modal-title]');
    if (titleEl) titleEl.textContent = product.title || '';

    // Price
    updatePrice();

    // Description (sanitized lightly — Shopify already returns trusted markup)
    var descEl = modal.querySelector('[data-cb-modal-description]');
    if (descEl) descEl.innerHTML = product.description || '';

    // Rating: clear existing then fetch + render asynchronously.
    // Judge.me's batch renderer (`window.jdgm.batchRenderBadges`) is only
    // available on PDPs — on collection pages, only the loader stubs
    // (`loadCSS`, `loadJS`) are loaded. So we fetch the product's PDP
    // HTML, parse out the rating data Judge.me already rendered there
    // (data-average-rating + data-number-of-reviews on .jdgm-prev-badge),
    // and render our own simple stars + count.
    var ratingEl = modal.querySelector('[data-cb-modal-rating]');
    if (ratingEl) {
      ratingEl.innerHTML = '';
      fetchAndRenderRating(product.handle, ratingEl);
    }

    // Images
    var trackEl = modal.querySelector('[data-cb-modal-track]');
    if (trackEl) {
      trackEl.innerHTML = '';
      var imageUrls = [];
      if (product.media && product.media.length) {
        product.media.forEach(function (m) {
          var src = m.preview_image && m.preview_image.src;
          if (src && imageUrls.indexOf(src) === -1) imageUrls.push(src);
        });
      } else if (product.images && product.images.length) {
        imageUrls = product.images.slice();
      } else if (product.featured_image) {
        imageUrls = [product.featured_image];
      }
      imageUrls.forEach(function (src, i) {
        var img = document.createElement('img');
        // Request a reasonable size from the CDN (Shopify supports inline width)
        var resized = src.indexOf('?') > -1
          ? src.replace(/(\.[a-z]+)(\?)/i, '$1$2width=900&')
          : src + '?width=900';
        img.src = resized;
        img.alt = product.title + ' — image ' + (i + 1);
        img.loading = i === 0 ? 'eager' : 'lazy';
        trackEl.appendChild(img);
      });
    }
    modalState.currentMediaIndex = 0;
    updateMediaTrack();

    // Variants
    var variantsEl = modal.querySelector('[data-cb-modal-variants]');
    if (variantsEl) {
      variantsEl.innerHTML = '';
      // Normalize options to a uniform [string] array regardless of API shape.
      var optionNamesNormalized = product.options.map(getOptionName);
      var hasMeaningfulOptions = optionNamesNormalized.length > 1
        || (optionNamesNormalized.length === 1 && optionNamesNormalized[0] !== 'Title');
      if (hasMeaningfulOptions) {
        optionNamesNormalized.forEach(function (optName, optionIndex) {
          if (optName === 'Title' && product.variants.length === 1) return;

          var values = [];
          product.variants.forEach(function (v) {
            var val = v.options[optionIndex];
            if (val && values.indexOf(val) === -1) values.push(val);
          });

          var group = document.createElement('div');
          group.className = 'cb-modal__variant-group';
          var label = document.createElement('div');
          label.className = 'cb-modal__variant-label';
          label.textContent = optName + ':';   // theme convention: "Color:" / "Length:"
          group.appendChild(label);
          var optionsRow = document.createElement('div');
          optionsRow.className = 'cb-modal__variant-options';
          values.forEach(function (val) {
            var matchingVariant = product.variants.find(function (v) {
              return v.options[optionIndex] === val;
            });
            var unavail = matchingVariant ? !matchingVariant.available : false;
            var selected = modalState.selectedOptions[optName] === val;
            optionsRow.appendChild(buildVariantOptionEl(optName, val, selected, unavail));
          });
          group.appendChild(optionsRow);
          variantsEl.appendChild(group);
        });
      }
    }

    // Step-aware info block. Mirrors the per-step explainer content from
    // the live PDPs into the modal so buyers see the same context
    // whether they reach the product via the Charm Bar flow or the
    // regular PDP.
    //   Step 1 (Chains): "Charm it your way" Linkies block with two
    //     icons. NOT shown for gemstone strands (CLUSONLY tag) — Linkies
    //     don't work on strands.
    //   Step 2 (Attachment): no step-info block (TBD if Greg adds copy).
    //   Step 3 (Charms): "Every charm comes with a Linkie™…" cart-aware
    //     block. Shown if the buyer's cart has a metal chain (or empty
    //     cart, default-show). Hidden ONLY when the cart contains a
    //     strand and zero metal chains — Linkies aren't relevant for
    //     strand-only buyers.
    var stepInfoEl = modal.querySelector('[data-cb-modal-step-info]');
    if (stepInfoEl) {
      stepInfoEl.innerHTML = '';
      var b = document.body;
      if (b.classList.contains('cb-on-step-1')) {
        // Strand exclusion: gemstone strands (CLUSONLY) don't take
        // Linkies, so the explainer doesn't apply. Check tags from
        // the fetched product.
        if (!isStrandProduct(product)) {
          stepInfoEl.innerHTML = renderChainLinkiesBlock();
        }
      } else if (b.classList.contains('cb-on-step-3')) {
        // Cart-aware: fetch /cart.js and decide whether to show the
        // charm Linkies block. Default to show on fetch error so the
        // info appears for buyers regardless of network glitches.
        shouldShowCharmLinkies(function (show) {
          if (show && stepInfoEl) {
            stepInfoEl.innerHTML = renderCharmLinkiesBlock();
          }
        });
      }
    }

    // Step-aware modal ATC button label.
    //   Step 1 (Chain) / Step 2 (Attachment) → "Select"
    //   Step 3 (Charm) → "Add"
    var atcBtn = modal.querySelector('[data-cb-modal-atc]');
    if (atcBtn) {
      var bodyClasses = document.body;
      atcBtn.textContent = bodyClasses.classList.contains('cb-on-step-3')
        ? 'Add'
        : 'Select';
    }
  }

  // True if a product fetched from /products/{handle}.js is a gemstone
  // strand (CLUSONLY tag). Strands don't support Linkies — the modal's
  // Linkies "Charm it your way" block is skipped for these.
  function isStrandProduct(product) {
    if (!product || !product.tags) return false;
    var tags = Array.isArray(product.tags) ? product.tags
      : (typeof product.tags === 'string' ? product.tags.split(',') : []);
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).trim() === 'CLUSONLY') return true;
    }
    return false;
  }

  // Decide whether to show the Step 3 charm modal's Linkies block.
  // Logic:
  //   cart empty     → show (default; buyer might add a chain later)
  //   metal chain    → show (Linkies are relevant)
  //   strand only    → HIDE (Linkies don't work on strands)
  //   both           → show (Greg's call: if cart has both, show)
  // Strand-only is the only state where we hide. The strand handle list
  // (window.__charmBarStrandHandles) is emitted from progress.liquid
  // by walking step_1_collection products and flagging any with the
  // CLUSONLY tag.
  function shouldShowCharmLinkies(callback) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
          callback(true);
          return;
        }
        var hasMetalChain = false;
        var hasStrand = false;
        var strandMap = window.__charmBarStrandHandles || {};
        var stepMap = window.__charmBarStepMap || {};
        cart.items.forEach(function (item) {
          var handle = normalizeHandle(item.handle || (item.product && item.product.handle));
          if (strandMap[handle]) {
            hasStrand = true;
          } else if (stepMap[handle] === 1) {
            // Step-1 item that isn't in the strand map → metal chain.
            hasMetalChain = true;
          }
        });
        // Only hide if strand-only.
        callback(!(hasStrand && !hasMetalChain));
      })
      .catch(function () { callback(true); });
  }

  // Linkies "Charm it your way" explainer for Step 1 (Chains) modal.
  // Pulled from the live chain PDPs (e.g. /products/large-flat-link-necklace).
  // Icons hosted on Shopify CDN — same URLs the PDP itself uses, so any
  // future swap on the PDP is one URL change away.
  // Learn more link opens in a new tab so buyers don't lose their place
  // in the builder. Greg also has a TODO to update the same link on the
  // PDPs themselves to use target="_blank" — currently it changes the page.
  // Charm modal (Step 3) Linkies copy. Mirrors the new charm PDP copy
  // Greg specified 2026-04-28: short paragraph + Learn more link that
  // opens in a new tab so the buyer doesn't lose their place in the
  // builder. Cart-aware: only rendered when shouldShowCharmLinkies()
  // returns true.
  function renderCharmLinkiesBlock() {
    return ''
      + '<div class="cb-modal__charm-linkies">'
      +   '<p class="cb-modal__charm-linkies-text">'
      +     'Every charm comes with a Linkie&trade;, so you can attach it to any chain in seconds.'
      +     '<br>'
      +     'Want to see how it works? '
      +     '<a href="https://www.kozakh.com/pages/linkie" target="_blank" rel="noopener">Learn more &rarr;</a>'
      +   '</p>'
      + '</div>';
  }

  function renderChainLinkiesBlock() {
    return ''
      + '<div class="cb-modal__linkies">'
      +   '<div class="cb-modal__linkies-intro">'
      +     'This chain is charm-ready with Linkies&trade; '
      +     '<a href="https://www.kozakh.com/pages/linkie" target="_blank" rel="noopener">Learn more</a>'
      +   '</div>'
      +   '<p class="cb-modal__linkies-title">Charm it your way:</p>'
      +   '<ul class="cb-modal__linkies-list">'
      +     '<li class="cb-modal__linkies-item">'
      +       '<img class="cb-modal__linkies-icon" '
      +            'src="https://cdn.shopify.com/s/files/1/0070/5934/1381/files/1_1_5c58cd2d-fc49-4e30-abf1-3ca07a676915.png?v=1758826039" '
      +            'alt="Charms permanently affixed along the strand" width="48" height="48" loading="lazy">'
      +       '<p class="cb-modal__linkies-text">Along the Chain with Linkies&trade;</p>'
      +     '</li>'
      +     '<li class="cb-modal__linkies-item">'
      +       '<img class="cb-modal__linkies-icon" '
      +            'src="https://cdn.shopify.com/s/files/1/0070/5934/1381/files/2_1.png?v=1758826040" '
      +            'alt="Charms clustered at the bottom using Linkies" width="48" height="48" loading="lazy">'
      +       '<p class="cb-modal__linkies-text">Clustered at the Bottom <span>with Linkies&trade;</span></p>'
      +     '</li>'
      +   '</ul>'
      +   '<p class="cb-modal__linkies-footer">Linkies&trade; let you move and change charms at any time. No tools required.</p>'
      + '</div>';
  }

  function showModal() {
    var modal = getModal();
    if (!modal) return;
    // Clear the inline pointer-events / visibility hides that we added
    // directly on the Liquid element to prevent FOUC click-capture on
    // first page load (before charm-bar.css has applied). Once cleared,
    // the CSS rules below take over: the [data-cb-modal-open="true"]
    // selector flips visibility:visible and pointer-events:auto.
    modal.style.pointerEvents = '';
    modal.style.visibility = '';
    modal.setAttribute('data-cb-modal-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cb-modal-open');
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.setAttribute('data-cb-modal-open', 'false');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cb-modal-open');
  }

  function openModalForProduct(handle) {
    if (!handle) return;
    fetch('/products/' + handle + '.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('product fetch failed');
        return res.json();
      })
      .then(function (product) {
        modalState.product = product;
        modalState.selectedOptions = {};
        // Default-select first available variant if any, else first variant
        var firstAvailable = product.variants.find(function (v) { return v.available; }) || product.variants[0];
        product.options.forEach(function (option, i) {
          var name = getOptionName(option);
          if (name) modalState.selectedOptions[name] = firstAvailable.options[i];
        });
        modalState.currentVariant = firstAvailable;
        populateModal();
        showModal();
      })
      .catch(function (err) {
        if (window.__charmBarDebug) console.warn('[charm-bar] modal fetch failed:', err);
      });
  }

  // --- Modal interactions -------------------------------------------------

  // --- Direct add-to-cart (bypasses modal) -------------------------------
  // Used by the Step 2 (Attachment) "Select" card button. Fetches the
  // product, picks the first available variant, POSTs to /cart/add.js,
  // dispatches cart events, then auto-advances the buyer to the next
  // step. The cart drawer is intentionally NOT opened — Greg's call
  // (2026-04-28): on the multi-step builder the drawer becomes friction;
  // buyers see what's been added via the cart-icon count badge in the
  // header and continue forward in the flow.
  function addProductToCartDirect(handle, btnEl) {
    if (!handle) return;
    var originalText = btnEl ? btnEl.textContent : '';
    if (btnEl) {
      btnEl.disabled = true;
      btnEl.textContent = 'Adding…';
    }
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed');
        return r.json();
      })
      .then(function (product) {
        var variant = product.variants.find(function (v) { return v.available; })
                   || product.variants[0];
        if (!variant) throw new Error('no variant available');
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: 1 })
        });
      })
      .then(function (r) { return r.json(); })
      .then(function () {
        if (btnEl) { btnEl.disabled = false; btnEl.textContent = originalText; }
        document.dispatchEvent(new CustomEvent('cart:added'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Auto-advance to next step. Step 2 → Step 3. (Step 1 doesn't
        // use direct-add because chain cards have no buttons; Step 3
        // doesn't auto-advance because buyers stack multiple charms.)
        if (document.body.classList.contains('cb-on-step-2')) {
          setTimeout(function () {
            window.location.href = '/collections/charm-bar-select-charm';
          }, 250);
        }
      })
      .catch(function (err) {
        if (btnEl) { btnEl.disabled = false; btnEl.textContent = originalText; }
        if (window.__charmBarDebug) console.warn('[charm-bar] direct ATC failed:', err);
      });
  }

  // --- Smart add: branches on variant count ------------------------------
  // Used by the Step 3 (Charms) Add to Cart button. Fetches the product:
  //   - If the product has multiple variants, we can't pick for the buyer —
  //     open the modal so they can choose. (Letter charm, Birthstone charm.)
  //   - If single-variant, add directly to cart, dispatch cart events, open
  //     the cart drawer. Same flow as Step 2's direct-add.
  // Note: openModalForProduct fetches the product again, which is fine —
  // the browser caches /products/{handle}.js so the second fetch is free,
  // and keeping the modal-open flow as a single function avoids forking
  // the modal initialization logic.
  function smartAddToCart(handle, btnEl) {
    if (!handle) return;
    var originalText = btnEl ? btnEl.textContent : '';
    if (btnEl) {
      btnEl.disabled = true;
      btnEl.textContent = 'Adding…';
    }
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch failed');
        return r.json();
      })
      .then(function (product) {
        if (product.variants && product.variants.length > 1) {
          // Multi-variant — restore button and let the modal take over.
          if (btnEl) { btnEl.disabled = false; btnEl.textContent = originalText; }
          openModalForProduct(handle);
          return null;
        }
        var variant = product.variants.find(function (v) { return v.available; })
                   || product.variants[0];
        if (!variant) throw new Error('no variant available');
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: 1 })
        }).then(function (r) { return r.json(); });
      })
      .then(function (result) {
        if (result === null) return;   // modal path, nothing more to do
        if (btnEl) { btnEl.disabled = false; btnEl.textContent = originalText; }
        document.dispatchEvent(new CustomEvent('cart:added'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Step 3 stays on the page after add — buyers stack multiple
        // charms manually before tapping View Cart. No drawer open
        // (Greg's call 2026-04-28); the cart-icon count badge in the
        // header gives buyers the running total feedback they need.
      })
      .catch(function (err) {
        if (btnEl) { btnEl.disabled = false; btnEl.textContent = originalText; }
        if (window.__charmBarDebug) console.warn('[charm-bar] smart ATC failed:', err);
      });
  }

  // Direct-add click handler — runs BEFORE the modal-opening handler.
  document.addEventListener('click', function (e) {
    var b = document.body;
    if (!b.classList.contains('cb-on-step-1')
        && !b.classList.contains('cb-on-step-2')
        && !b.classList.contains('cb-on-step-3')) return;

    var target = e.target.closest('.cb-card-actions__direct-add');
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();   // prevent the modal-opening handler below from also firing

    var card = target.closest('product-card');
    if (!card) return;
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var handle = link.getAttribute('href').split('/products/')[1];
    if (!handle) return;
    handle = handle.split('?')[0].split('#')[0].split('/')[0];
    addProductToCartDirect(handle, target);
  });

  // Smart-add click handler for the Step 3 (Charms) Add to Cart button.
  // The Charms collection contains a mix of single-variant charms (most)
  // and multi-variant charms (Letter charm — 26 letters; Birthstone charm —
  // 12 stones). For single-variant charms, ATC should add immediately
  // bypassing the modal — the buyer's already chosen which charm. For
  // multi-variant charms, ATC needs to open the modal so the buyer can
  // pick their variant. We can't decide until we know the variant count,
  // so we fetch the product on click and branch.
  //
  // Runs BEFORE the generic openModal handler (which still handles View,
  // image-click, and title-click). Stops propagation so that handler
  // doesn't double-fire and open the modal we just decided NOT to open.
  document.addEventListener('click', function (e) {
    var b = document.body;
    if (!b.classList.contains('cb-on-step-3')) return;

    var target = e.target.closest('.cb-card-actions__add');
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();

    var card = target.closest('product-card');
    if (!card) return;
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var handle = link.getAttribute('href').split('/products/')[1];
    if (!handle) return;
    handle = handle.split('?')[0].split('#')[0].split('/')[0];
    smartAddToCart(handle, target);
  });

  // Card interactions on Charm Bar collection pages: open custom modal
  // when the buyer taps the product image or title.
  //   - On Step 1 (Chains) the image/title click is the ONLY way to open
  //     the modal — there are no buttons on the card.
  //   - On Step 2 (Attachment) and Step 3 (Charms) the Select button
  //     direct-adds (Step 2) or smart-adds (Step 3) without opening the
  //     modal; image/title clicks here are the "I want details first"
  //     affordance.
  // For image/title we MUST preventDefault — they're <a> tags pointing
  // at /products/{handle} that would otherwise navigate away.
  // NOTE: cb-card-actions__add and cb-card-actions__direct-add are
  // handled by their own dedicated click handlers above — they should
  // NOT fall through to the modal-opening path here.
  // Scoped to step pages only via body class so we don't intercept
  // clicks on the landing page or anywhere else.
  document.addEventListener('click', function (e) {
    var b = document.body;
    if (!b.classList.contains('cb-on-step-1')
        && !b.classList.contains('cb-on-step-2')
        && !b.classList.contains('cb-on-step-3')) return;

    var target = e.target.closest(
      'product-card a.product-card__media, '
      + 'product-card a.product-title'
    );
    if (!target) return;
    e.preventDefault();

    var card = target.closest('product-card');
    if (!card) return;
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var handle = link.getAttribute('href').split('/products/')[1];
    if (!handle) return;
    handle = handle.split('?')[0].split('#')[0].split('/')[0];
    openModalForProduct(handle);
  });

  // Variant pill clicks
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cb-modal__variant-option');
    if (!btn) return;
    if (btn.dataset.unavailable === 'true') return;
    var optionName = btn.dataset.optionName;
    var optionValue = btn.dataset.optionValue;
    if (!optionName) return;

    modalState.selectedOptions[optionName] = optionValue;
    var variant = findVariantForOptions();
    if (variant) {
      modalState.currentVariant = variant;

      // Update selected styling for this option group only
      var modal = getModal();
      modal.querySelectorAll('.cb-modal__variant-option').forEach(function (opt) {
        if (opt.dataset.optionName === optionName) {
          opt.dataset.selected = (opt.dataset.optionValue === optionValue) ? 'true' : 'false';
        }
      });

      updatePrice();

      // Jump to variant's featured image if it has one
      if (variant.featured_image && variant.featured_image.src) {
        var imgs = modal.querySelectorAll('[data-cb-modal-track] img');
        var targetSrc = variant.featured_image.src;
        for (var i = 0; i < imgs.length; i++) {
          if (imgs[i].src.indexOf(targetSrc.split('?')[0]) !== -1) {
            modalState.currentMediaIndex = i;
            updateMediaTrack();
            break;
          }
        }
      }
    }
  });

  // Image gallery prev/next
  document.addEventListener('click', function (e) {
    var modal = getModal();
    if (!modal) return;
    var track = modal.querySelector('[data-cb-modal-track]');
    if (!track) return;
    if (e.target.closest('[data-cb-modal-prev]')) {
      if (modalState.currentMediaIndex > 0) {
        modalState.currentMediaIndex--;
        updateMediaTrack();
      }
    } else if (e.target.closest('[data-cb-modal-next]')) {
      if (modalState.currentMediaIndex < track.children.length - 1) {
        modalState.currentMediaIndex++;
        updateMediaTrack();
      }
    }
  });

  // ATC inside modal (button labeled "Select" in the UI). Adds the
  // current variant, closes the modal, and auto-advances if the buyer
  // is on Step 1 (Chains). Step 2 uses the card-level direct-add path
  // and Step 3 stays on the page so buyers can stack charms — neither
  // hits this modal-ATC code path with auto-advance.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cb-modal-atc]');
    if (!btn) return;
    if (!modalState.currentVariant) return;
    btn.dataset.loading = 'true';
    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: modalState.currentVariant.id, quantity: 1 })
    })
      .then(function (res) { return res.json(); })
      .then(function () {
        btn.disabled = false;
        btn.textContent = originalText;
        delete btn.dataset.loading;
        closeModal();
        document.dispatchEvent(new CustomEvent('cart:added'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Auto-advance from Step 1 → Step 2 after a chain is added via
        // the modal. Cart drawer is intentionally not opened — buyers
        // see the running cart count via the header cart-icon badge.
        if (document.body.classList.contains('cb-on-step-1')) {
          setTimeout(function () {
            window.location.href = '/collections/attachment-style';
          }, 250);
        }
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = originalText;
        delete btn.dataset.loading;
        if (window.__charmBarDebug) console.warn('[charm-bar] ATC failed:', err);
      });
  });

  // Backdrop / close button click
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cb-modal-backdrop]') || e.target.closest('[data-cb-modal-close]')) {
      closeModal();
    }
  });

  // Escape key closes modal
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var modal = getModal();
      if (modal && modal.getAttribute('data-cb-modal-open') === 'true') {
        closeModal();
      }
    }
  });

  // --- Auto-advance after ATC --------------------------------------------
  // REMOVED 2026-04-28. Previously this function set up a MutationObserver
  // on <cart-drawer> to detect when the drawer closed after an item was
  // added, then auto-navigated to the next step. With the cart drawer
  // open-on-add behavior removed (Greg's call: drawer is friction in a
  // multi-step builder), the drawer never opens, so the observer would
  // never fire. Auto-advance is now done inline in the three add paths:
  //
  //   - Modal ATC (Step 1, chains): navigate to /attachment-style after
  //     the variant is added (see modal ATC handler above).
  //   - addProductToCartDirect (Step 2, attachment): navigate to
  //     /charm-bar-select-charm after add (see helper above).
  //   - smartAddToCart (Step 3, charms): no navigate — buyers stack
  //     multiple charms manually before tapping View Cart.
  //
  // Buyers see what's been added via the cart-icon count badge in the
  // header. The dead setupAutoAdvance() function and its
  // <cart-drawer> MutationObserver are gone.

  // --- Sticky header stack height measurement ----------------------------
  // The KOZAKH theme has its own sticky header stack (announcement bar +
  // main header). Their combined height differs by viewport (and changes
  // if the announcement bar closes). We measure the total at runtime and
  // expose it as --cb-header-stack-height so the progress bar's wrapper
  // sticks at the correct offset on every viewport.

  function setHeaderStackHeight() {
    var sections = document.querySelectorAll('.shopify-section-group-header-group');
    var total = 0;
    sections.forEach(function (el) {
      var cs = window.getComputedStyle(el);
      // Only count elements that are part of the sticky stack
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        total += el.getBoundingClientRect().height;
      }
    });
    if (total > 0) {
      document.documentElement.style.setProperty('--cb-header-stack-height', total + 'px');
    }
  }

  // Update on window resize (debounced)
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setHeaderStackHeight, 100);
  });

  // --- Master refresh -----------------------------------------------------

  function refreshAll() {
    fetchCart()
      .then(function (cart) {
        var buckets = bucketCartByStep(cart);
        updateProgressChips(buckets);
        updateActionBar(buckets);
      })
      .catch(function (err) {
        // Silent fail on cart fetch errors — the static Liquid render is the
        // fallback. Don't spam the console.
        if (window.__charmBarDebug) console.warn('[charm-bar]', err);
      });
    updateFilterCountBadge();
  }

  // --- Wire up event listeners --------------------------------------------

  // 1. View Cart button — anything with data-cart-drawer-trigger
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-drawer-trigger]');
    if (!btn) return;
    e.preventDefault();
    openCartDrawer();
  });

  // 2. Mobile filter trigger pill
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cb-filter-trigger');
    if (!btn) return;
    e.preventDefault();
    openFiltersDrawer();
  });

  // 3. Theme cart events — refresh state whenever cart changes
  ['cart:change', 'cart:refresh', 'cart:added', 'cart:updated'].forEach(function (eventName) {
    document.addEventListener(eventName, refreshAll);
  });

  // Tag the body with a per-step class so CSS can scope rules per page
  // (centering attachment grid on desktop, hiding the filter toolbar on
  // Step 2 only, etc.). Using URL pattern matching so the tag stays in
  // sync regardless of template assignment.
  function setStepBodyClass() {
    var b = document.body;
    if (!b) return;
    b.classList.remove('cb-on-step-1', 'cb-on-step-2', 'cb-on-step-3', 'cb-on-landing');
    var p = location.pathname;
    if (/\/collections\/charm-bar-select-chain/.test(p)) b.classList.add('cb-on-step-1');
    else if (/\/collections\/attachment-style/.test(p)) b.classList.add('cb-on-step-2');
    else if (/\/collections\/charm-bar-select-charm/.test(p)) b.classList.add('cb-on-step-3');
    else if (/\/pages\/charm-bar/.test(p)) b.classList.add('cb-on-landing');
  }

  // Portal the modal element to be a direct child of <body> so its
  // z-index: 1000 takes effect at the page-level stacking context.
  // The modal lives inside the progress bar's section by default
  // (rendered in sections/charm-bar-progress.liquid), but that section
  // creates its own low-z stacking context (sticky + z-index: 3),
  // which traps the modal underneath the header. Moving it to body
  // escapes that trap.
  function portalModalToBody() {
    var modal = document.querySelector('[data-cb-modal]');
    if (!modal) return;
    if (modal.parentElement === document.body) return;  // already portaled
    document.body.appendChild(modal);
  }

  // 4. Page lifecycle events
  function onReady() {
    setStepBodyClass();
    setHeaderStackHeight();
    portalModalToBody();
    injectCardActions();
    refreshAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    // DOM is already ready (script loaded with defer or after body)
    onReady();
  }

  // pageshow fires on back/forward cache restoration — refresh state
  window.addEventListener('pageshow', function () {
    setHeaderStackHeight();
    refreshAll();
  });

  // 5. Re-inject card actions if the theme re-renders the product grid
  // (e.g., via filter changes which can replace the grid in place).
  // MutationObserver on the product grid container.
  function watchProductGrid() {
    var grid = document.querySelector('product-list, [class*="product-grid"], [class*="product-list"]');
    if (!grid) return;
    var observer = new MutationObserver(function () {
      injectCardActions();
    });
    observer.observe(grid, { childList: true, subtree: true });
  }
  watchProductGrid();

  // Expose for debugging
  window.__charmBar = {
    refreshAll: refreshAll,
    injectCardActions: injectCardActions,
    openCartDrawer: openCartDrawer,
    openFiltersDrawer: openFiltersDrawer,
    setHeaderStackHeight: setHeaderStackHeight,
    openModalForProduct: openModalForProduct,
    closeModal: closeModal,
    modalState: modalState,
    stepMap: stepMap
  };
})();